import { PlayerName } from "@/components/PlayerCardDialog";
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Radio } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIT = {
  TARGET_HIT: { e: "🟢", c: "text-lime-300", l: "TARGET HIT" },
  SECONDARY_HIT: { e: "🟡", c: "text-amber-300", l: "SECONDARY" },
  GOAL: { e: "⚫", c: "text-slate-300", l: "GOAL" },
};
const STATUS = {
  HIT: "text-lime-300", MISS: "text-rose-400", PENDING: "text-slate-500",
  GOAL: "text-lime-300", "NO GOAL": "text-rose-400",
};
const RESULT = {
  HIT: "🟢 HIT", PARTIAL: "🟡 PARTIAL", MISS: "🔴 MISS", PENDING: "⚪ PENDING",
};
const STATE = {
  LIVE: "text-lime-300 bg-lime-950/50 border-lime-500/40",
  FINAL: "text-slate-400 bg-slate-800/50 border-slate-600/40",
  PENDING: "text-cyan-300 bg-cyan-950/40 border-cyan-500/30",
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-1.5">
    <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-mono text-sm font-bold text-cyan-300">{value ?? "—"}</div>
  </div>
);

export default function LiveTrackerTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    silent ? setRefreshing(true) : setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/api/live-tracker`, { params: apiDate ? { date: apiDate } : {}, withCredentials: true });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load live tracker"); }
    finally { setLoading(false); setRefreshing(false); }
  }, [apiDate]);

  useEffect(() => { load(); }, [load]);

  // auto-refresh only while games are live (credit-light: single endpoint)
  useEffect(() => {
    const live = data?.summary?.games_live > 0;
    if (timer.current) clearInterval(timer.current);
    if (live) timer.current = setInterval(() => load(true), 60000);
    return () => timer.current && clearInterval(timer.current);
  }, [data?.summary?.games_live, load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="live-tracker-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Loading live goals…</p>
    </div>
  );
  if (err) return <div className="py-20 text-center font-mono text-sm text-rose-400" data-testid="live-tracker-error">{err}</div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="live-tracker-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-4">
        <h2 className="flex items-center gap-2 font-mono text-lg font-black uppercase tracking-wider text-cyan-300">
          <Radio className="h-5 w-5 text-lime-400" /> Live Tracker / Auditor
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Stat label="Live" value={s.games_live} />
          <Stat label="Final" value={s.games_final} />
          <Stat label="Pos Hit%" value={s.positional_hit_rate != null ? `${s.positional_hit_rate}%` : "—"} />
          <Stat label="Pair Hit%" value={s.pairing_hit_rate != null ? `${s.pairing_hit_rate}%` : "—"} />
          <Stat label="Targeted G" value={s.goals_targeted} />
          <Stat label="Other G" value={s.goals_non_targeted} />
          <button onClick={() => load(true)} data-testid="live-refresh-btn" className="flex items-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-cyan-300 hover:bg-cyan-950/60">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* GAMES */}
      <div className="grid gap-4 lg:grid-cols-2">
        {data.games.map((g) => (
          <div key={g.game_id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={`live-game-${g.away}-${g.home}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono text-sm font-bold text-slate-100">{g.away} {g.away_score ?? 0} — {g.home_score ?? 0} {g.home}</div>
              <span className={`rounded border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${STATE[g.state] || STATE.PENDING}`}>{g.state}</span>
            </div>

            {/* targeted positions */}
            <div className="mt-2 flex flex-wrap gap-2 font-mono text-[10px]">
              {Object.entries(g.targets).map(([tm, rows]) => rows.map((r) => (
                <span key={`${tm}-${r.position}`} className={`rounded bg-slate-800/60 px-1.5 py-0.5 ${STATUS[r.status]}`}>
                  {tm} {r.position} {r.rank === 1 ? "🎯" : "·"} {r.opp_ga_pos} · {r.status}
                </span>
              )))}
            </div>

            {/* goal feed */}
            <div className="mt-3 space-y-1">
              {g.goals.length === 0 ? (
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-600">⚪ No goals yet</p>
              ) : g.goals.map((go, i) => {
                const a = AUDIT[go.audit] || AUDIT.GOAL;
                return (
                  <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded bg-slate-950/50 px-2 py-1 font-mono text-[11px]" data-testid={`live-goal-${g.game_id}-${i}`}>
                    <span>{a.e}</span>
                    <span className="font-bold text-slate-100">{go.team}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-cyan-300">{go.line || "?"}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-amber-300">{go.position || "?"}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-slate-300">#{go.jersey ?? "?"} <PlayerName name={go.player} team={go.team} /></span>
                    <span className={`${a.c}`}>{a.l}</span>
                    <span className="text-slate-600">P{go.period} {go.time}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* PAIRING AUDIT */}
      <div>
        <h3 className="mb-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-400">Pairing Audit (vs Cheat Sheet)</h3>
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full min-w-[560px] text-left font-mono text-[11px]">
            <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2">Pairing</th><th className="px-3 py-2">Unit A</th><th className="px-3 py-2">Unit B</th><th className="px-3 py-2 text-right">Result</th></tr>
            </thead>
            <tbody>
              {data.pairing_audit.map((p) => (
                <tr key={p.pair_id} className="border-t border-slate-800/60" data-testid={`pair-audit-${p.pair_id}`}>
                  <td className="px-3 py-2 font-bold text-slate-100">{p.label}</td>
                  <td className={`px-3 py-2 ${STATUS[p.unit_a.status]}`}>{p.unit_a.team} {p.unit_a.position} — {p.unit_a.status}</td>
                  <td className={`px-3 py-2 ${STATUS[p.unit_b.status]}`}>{p.unit_b.team} {p.unit_b.position} — {p.unit_b.status}</td>
                  <td className="px-3 py-2 text-right">{RESULT[p.result]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
